/**
 * Chat Store
 *
 * Centralized state management for chat messages and conversation state.
 * Uses a simple reactive pattern with subscribers.
 */

import {
    CHAT_STORAGE_VERSION,
    areChatStorageContextsEqual,
    localStore,
    normalizeChatStorageContext,
    migrateConversationIdStorage,
    sessionStore,
    generateUUID,
    STORAGE_KEYS
} from '../utils/storage.js';

/**
 * Message types used in the chat
 */
export const MESSAGE_TYPES = {
    USER: 'user',
    BOT: 'bot',
    STREAMING: 'streaming',
    GENESYS_USER: 'genesys_user',
    GENESYS_AGENT: 'genesys_agent',
    GENESYS_BOT: 'genesys_bot',
    GENESYS_STATUS: 'genesys_status',
    ADDRESS_FOLLOW_UP: 'address_follow_up',
    ADDRESS_CARD: 'address_card',
    GET_STARTED_CARD: 'get_started_card'
};

function parseMessageTimestamp(value) {
    if (!value) return null;

    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
}

function compareStructuredHistoryEntries(left, right) {
    const leftTimestamp = parseMessageTimestamp(left.created_at || left.createdAt);
    const rightTimestamp = parseMessageTimestamp(right.created_at || right.createdAt);

    if (leftTimestamp !== null && rightTimestamp !== null && leftTimestamp !== rightTimestamp) {
        return leftTimestamp - rightTimestamp;
    }

    if (leftTimestamp !== null && rightTimestamp === null) {
        return -1;
    }

    if (leftTimestamp === null && rightTimestamp !== null) {
        return 1;
    }

    const leftId = left.id || '';
    const rightId = right.id || '';
    if (leftId && rightId && leftId !== rightId) {
        return leftId.localeCompare(rightId);
    }

    return 0;
}

/**
 * ChatStore class - manages chat state
 */
class ChatStore {
    constructor() {
        // Chat messages array
        this._messages = [];

        // Suggestions array (persisted)
        this._suggestions = [];

        // Session identifiers
        this._conversationId = null;
        this._businessRequestId = null;
        this._chatSessionId = null;
        this._ownerType = null;

        // Server-issued ResourceAPI selectors (v4). Distinct from _conversationId
        // (the client UUID) — a server assist_conversation_id is never written to it.
        this._resourceApiPlanningApplicationId = null;
        this._resourceApiAssistConversationId = null;

        // Loading states
        this._isLoading = false;
        this._isStreaming = false;

        // Genesys handover state
        this._isHandoverActive = false;
        this._genesysSessionToken = null;

        // Track address cards that failed validation
        this._invalidAddressCards = [];

        // Active tenant/council context used for persisted chat state.
        this._storageContext = null;

        // Subscribers for reactive updates
        this._subscribers = new Set();

        // Initialize session IDs
        this._initializeSessionIds();

        // Load suggestions from storage
        this._loadSuggestions();
    }

    /**
     * Initialize or restore session identifiers
     */
    _initializeSessionIds() {
        // Try to restore from sessionStorage
        this._conversationId = migrateConversationIdStorage();
        this._businessRequestId = sessionStore.get(STORAGE_KEYS.BUSINESS_REQUEST_ID);
        this._chatSessionId = sessionStore.get(STORAGE_KEYS.CHAT_SESSION_ID);
        this._ownerType = sessionStore.get(STORAGE_KEYS.CHAT_OWNER_TYPE);
        this._resourceApiPlanningApplicationId = sessionStore.get(STORAGE_KEYS.RESOURCEAPI_PLANNING_APPLICATION_ID);
        this._resourceApiAssistConversationId = sessionStore.get(STORAGE_KEYS.RESOURCEAPI_ASSIST_CONVERSATION_ID);

        // Generate new ID if not found
        if (!this._conversationId) {
            this._conversationId = generateUUID();
            this._persistConversationId();
        }
    }

    _persistConversationId() {
        sessionStore.set(STORAGE_KEYS.CONVERSATION_ID, this._conversationId);
    }

    _persistBusinessRequestContext() {
        if (this._businessRequestId) {
            sessionStore.set(STORAGE_KEYS.BUSINESS_REQUEST_ID, this._businessRequestId);
        } else {
            sessionStore.remove(STORAGE_KEYS.BUSINESS_REQUEST_ID);
        }

        if (this._chatSessionId) {
            sessionStore.set(STORAGE_KEYS.CHAT_SESSION_ID, this._chatSessionId);
        } else {
            sessionStore.remove(STORAGE_KEYS.CHAT_SESSION_ID);
        }

        if (this._ownerType) {
            sessionStore.set(STORAGE_KEYS.CHAT_OWNER_TYPE, this._ownerType);
        } else {
            sessionStore.remove(STORAGE_KEYS.CHAT_OWNER_TYPE);
        }

        if (this._resourceApiPlanningApplicationId) {
            sessionStore.set(STORAGE_KEYS.RESOURCEAPI_PLANNING_APPLICATION_ID, this._resourceApiPlanningApplicationId);
        } else {
            sessionStore.remove(STORAGE_KEYS.RESOURCEAPI_PLANNING_APPLICATION_ID);
        }

        if (this._resourceApiAssistConversationId) {
            sessionStore.set(STORAGE_KEYS.RESOURCEAPI_ASSIST_CONVERSATION_ID, this._resourceApiAssistConversationId);
        } else {
            sessionStore.remove(STORAGE_KEYS.RESOURCEAPI_ASSIST_CONVERSATION_ID);
        }
    }

    /**
     * Subscribe to state changes
     * @param {Function} callback - Called when state changes
     * @returns {Function} - Unsubscribe function
     */
    subscribe(callback) {
        this._subscribers.add(callback);
        // Immediately call with current state
        callback(this.getState());
        return () => this._subscribers.delete(callback);
    }

    /**
     * Notify all subscribers of state changes
     */
    _notify() {
        const state = this.getState();
        this._subscribers.forEach((callback) => callback(state));
    }

    /**
     * Get current state snapshot
     * @returns {Object} - Current state
     */
    getState() {
        return {
            messages: [...this._messages],
            suggestions: [...this._suggestions],
            conversationId: this._conversationId,
            businessRequestId: this._businessRequestId,
            chatSessionId: this._chatSessionId,
            ownerType: this._ownerType,
            resourceApiPlanningApplicationId: this._resourceApiPlanningApplicationId,
            resourceApiAssistConversationId: this._resourceApiAssistConversationId,
            isLoading: this._isLoading,
            isStreaming: this._isStreaming,
            isHandoverActive: this._isHandoverActive,
            genesysSessionToken: this._genesysSessionToken,
            invalidAddressCards: [...this._invalidAddressCards]
        };
    }

    // ==================== Message Management ====================

    /**
     * Load messages from localStorage
     */
    loadFromStorage(context = this._storageContext) {
        const normalizedContext = normalizeChatStorageContext(context);
        if (normalizedContext) {
            this._storageContext = normalizedContext;
        }

        const stored = localStore.getJSON(STORAGE_KEYS.CHAT_HISTORY);

        if (this._storageContext) {
            if (
                stored &&
                typeof stored === 'object' &&
                stored.version === CHAT_STORAGE_VERSION &&
                areChatStorageContextsEqual(stored.context, this._storageContext) &&
                Array.isArray(stored.messages)
            ) {
                this._messages = stored.messages;
                this._notify();
                return;
            }

            if (stored) {
                localStore.remove(STORAGE_KEYS.CHAT_HISTORY);
                this._messages = [];
                this._notify();
            }
            return;
        }

        if (stored && Array.isArray(stored)) {
            this._messages = stored;
            this._notify();
        }
    }

    /**
     * Save messages to localStorage
     */
    _persistMessages() {
        if (this._storageContext) {
            localStore.setJSON(STORAGE_KEYS.CHAT_HISTORY, {
                version: CHAT_STORAGE_VERSION,
                context: this._storageContext,
                messages: this._messages
            });
            return;
        }

        localStore.setJSON(STORAGE_KEYS.CHAT_HISTORY, this._messages);
    }

    setStorageContext(context, { reset = false } = {}) {
        this._storageContext = normalizeChatStorageContext(context);
        if (reset) {
            this.resetTenantState();
            return;
        }

        this._loadSuggestions();
    }

    resetTenantState() {
        this._messages = [];
        this._suggestions = [];
        this._invalidAddressCards = [];
        this._businessRequestId = null;
        this._chatSessionId = null;
        this._ownerType = null;
        this._resourceApiPlanningApplicationId = null;
        this._resourceApiAssistConversationId = null;
        this._isLoading = false;
        this._isStreaming = false;
        this._isHandoverActive = false;
        this._genesysSessionToken = null;
        this._conversationId = generateUUID();

        try {
            sessionStorage.removeItem('invalidAddressCards');
        } catch {
            /* ignore */
        }

        sessionStore.remove(STORAGE_KEYS.GENESYS_SESSION_TOKEN);
        this._persistConversationId();
        this._persistBusinessRequestContext();
        this._persistMessages();
        this._persistSuggestions();
        this._notify();
    }

    /**
     * Add a message to the chat
     * @param {Object} message - Message object
     * @param {string} message.text - Message content
     * @param {string} message.type - Message type (from MESSAGE_TYPES)
     * @param {string} [message.sender] - 'user' or 'bot'
     * @returns {string} - Message ID
     */
    addMessage({
        text,
        type,
        sender = 'bot',
        genesysSessionId = null,
        agentName = null,
        persistedMessageId = null,
        feedback = null,
        businessRequestId = this._businessRequestId,
        chatSessionId = this._chatSessionId,
        ownerType = this._ownerType
    }) {
        const message = {
            id: generateUUID(),
            text,
            type: type || (sender === 'user' ? MESSAGE_TYPES.USER : MESSAGE_TYPES.BOT),
            sender,
            timestamp: new Date().toISOString(),
            genesysSessionId,
            persistedMessageId,
            feedback,
            businessRequestId,
            chatSessionId,
            ownerType,
            ...(agentName && { agentName })
        };

        this._messages = [...this._messages, message];
        this._persistMessages();
        this._notify();

        return message.id;
    }

    /**
     * Add a user message
     * @param {string} text - Message text
     * @param {string} [type] - Optional message type override
     * @returns {string} - Message ID
     */
    addUserMessage(text, type = MESSAGE_TYPES.USER) {
        return this.addMessage({
            text,
            type,
            sender: 'user',
            genesysSessionId: this._isHandoverActive ? this._genesysSessionToken : null
        });
    }

    /**
     * Add a bot message
     * @param {string} text - Message text
     * @param {string} [type] - Optional message type override
     * @returns {string} - Message ID
     */
    addBotMessage(text, type = MESSAGE_TYPES.BOT) {
        return this.addMessage({
            text,
            type,
            sender: 'bot',
            genesysSessionId: this._isHandoverActive ? this._genesysSessionToken : null
        });
    }

    /**
     * Add an address card message to the chat
     * @param {string} address - The address value (empty for new, filled for disabled)
     * @param {boolean} disabled - Whether the card is disabled
     * @returns {string} - Message ID
     */
    addAddressCardMessage(address = '', disabled = false) {
        const message = {
            id: generateUUID(),
            text: address,
            type: MESSAGE_TYPES.ADDRESS_CARD,
            sender: 'bot',
            timestamp: new Date().toISOString(),
            persistedMessageId: null,
            businessRequestId: this._businessRequestId,
            chatSessionId: this._chatSessionId,
            ownerType: this._ownerType,
            disabled: disabled
        };

        this._messages = [...this._messages, message];
        this._notify();
        this._persistMessages();
        return message.id;
    }

    /**
     * Update an address card message (e.g., to disable it after submission)
     * @param {string} id - Message ID
     * @param {Object} updates - Updates to apply
     */
    updateAddressCardMessage(id, updates) {
        this._messages = this._messages.map((msg) => (msg.id === id ? { ...msg, ...updates } : msg));
        this._notify();
        this._persistMessages();
    }

    /**
     * Record an address card that failed validation
     * @param {string} id - Address card message ID
     * @param {string} invalidValue - The address input that failed
     */
    addInvalidAddressCard(id, invalidValue) {
        this._invalidAddressCards = [...this._invalidAddressCards, { id, invalidValue }];
        this._notify();
    }

    /**
     * Add a get started card message to the chat
     * @param {string} portalUrl - The portal URL for the Start now button
     * @returns {string} - Message ID
     */
    addGetStartedCardMessage(portalUrl = '') {
        const message = {
            id: generateUUID(),
            text: '',
            type: MESSAGE_TYPES.GET_STARTED_CARD,
            sender: 'bot',
            timestamp: new Date().toISOString(),
            persistedMessageId: null,
            businessRequestId: this._businessRequestId,
            chatSessionId: this._chatSessionId,
            ownerType: this._ownerType,
            portalUrl: portalUrl
        };

        this._messages = [...this._messages, message];
        this._notify();
        this._persistMessages();
        return message.id;
    }

    /**
     * Remove a get started card message by ID
     * @param {string} id - Message ID
     */
    removeGetStartedCardMessage(id) {
        this._messages = this._messages.filter((msg) => msg.id !== id);
        this._notify();
        this._persistMessages();
    }

    updateGetStartedCardLoading(id, isLoading) {
        const index = this._messages.findIndex((msg) => msg.id === id);
        if (index !== -1) {
            this._messages[index] = {
                ...this._messages[index],
                isLoading: isLoading
            };
            this._notify();
        }
    }

    /**
     * Set error state on a get started card
     * @param {string} id - Message ID
     * @param {string} errorMessage - Error message to display
     */
    updateGetStartedCardError(id, errorMessage) {
        const index = this._messages.findIndex((msg) => msg.id === id);
        if (index !== -1) {
            this._messages[index] = {
                ...this._messages[index],
                isLoading: false,
                errorMessage
            };
            this._notify();
        }
    }

    /**
     * Create a streaming message placeholder
     * @returns {string} - Message ID for updating
     */
    createStreamingMessage() {
        const message = {
            id: generateUUID(),
            text: '',
            type: MESSAGE_TYPES.STREAMING,
            sender: 'bot',
            timestamp: new Date().toISOString(),
            persistedMessageId: null,
            feedback: null,
            businessRequestId: this._businessRequestId,
            chatSessionId: this._chatSessionId,
            ownerType: this._ownerType
        };

        this._messages = [...this._messages, message];
        this._isStreaming = true;
        this._notify();
        return message.id;
    }

    /**
     * Update a streaming message with new content
     * @param {string} id - Message ID
     * @param {string} text - New text content (replaces existing)
     */
    updateStreamingMessage(id, text) {
        const index = this._messages.findIndex((m) => m.id === id);
        if (index !== -1) {
            this._messages = this._messages.map((msg, i) => (i === index ? { ...msg, text } : msg));
            // Don't persist during streaming, only notify UI
            this._notify();
        }
    }

    /**
     * Append text to a streaming message
     * @param {string} id - Message ID
     * @param {string} chunk - Text chunk to append
     */
    appendToStreamingMessage(id, chunk) {
        const message = this._messages.find((m) => m.id === id);
        if (message) {
            // Don't add a space before or after newline tokens — preserve markdown line breaks
            const isNewlineChunk = /^\n+$/.test(chunk) || chunk.startsWith('\n');
            const endsWithSpace = message.text.endsWith(' ') || message.text.endsWith('\n');
            const separator = !message.text || isNewlineChunk || endsWithSpace ? '' : ' ';
            this.updateStreamingMessage(id, message.text + separator + chunk);
        }
    }

    /**
     * Finalize a streaming message (convert to regular bot message)
     * @param {string} id - Message ID
     */
    finalizeStreamingMessage(id) {
        const index = this._messages.findIndex((m) => m.id === id);
        if (index !== -1) {
            const message = this._messages[index];
            // Remove empty streaming messages instead of persisting
            if (!message.text?.trim()) {
                this._messages = this._messages.filter((_, i) => i !== index);
            } else {
                this._messages = this._messages.map((msg, i) =>
                    i === index ? { ...msg, type: MESSAGE_TYPES.BOT } : msg
                );
            }
            this._isStreaming = false;
            this._persistMessages();
            this._notify();
        }
    }

    /**
     * Add a bot message with streaming animation (for preset messages)
     * Simulates the streaming effect by adding text word by word
     * @param {string} text - Full message text
     * @param {number} [delay=30] - Delay between words in milliseconds
     * @param {string} [existingMessageId=null] - Optional existing streaming message ID to reuse
     * @param {Object} [options] - Streaming options
     * @param {Function} [options.shouldContinue] - Returns false when the caller has been invalidated
     * @returns {Promise<string>} - Message ID
     */
    async addBotMessageWithStreaming(text, delay = 30, existingMessageId = null, options = {}) {
        if (!text) return null;
        const shouldContinue = () => options.shouldContinue?.() ?? true;
        if (!shouldContinue()) return null;

        chatStore.setLoading(true);

        // Use existing message ID or create new streaming message
        const messageId = existingMessageId || this.createStreamingMessage();

        // Split text into words, preserving HTML tags as single units
        const tokens = this._tokenizeForStreaming(text);

        // Stream each token with delay
        for (let i = 0; i < tokens.length; i++) {
            if (!shouldContinue()) {
                chatStore.setLoading(false);
                return null;
            }

            const token = tokens[i];
            this.appendToStreamingMessage(messageId, token);

            // Add delay between tokens (skip delay for last token)
            if (i < tokens.length - 1) {
                await new Promise((resolve) => setTimeout(resolve, delay));
            }
        }

        if (!shouldContinue()) {
            chatStore.setLoading(false);
            return null;
        }

        // Finalize the message
        this.finalizeStreamingMessage(messageId);
        chatStore.setLoading(false);

        return messageId;
    }

    /**
     * Tokenize text for streaming, keeping HTML tags intact
     * @param {string} text - Text to tokenize
     * @returns {Array<string>} - Array of tokens
     */
    _tokenizeForStreaming(text) {
        const tokens = [];
        // Regex to match HTML tags or words/spaces
        const regex = /(<[^>]+>)|(\s+)|([^\s<]+)/g;
        let match;

        while ((match = regex.exec(text)) !== null) {
            if (match[1]) {
                // HTML tag - add as single token
                tokens.push(match[1]);
            } else if (match[2]) {
                // Whitespace
                tokens.push(match[2]);
            } else if (match[3]) {
                // Word
                tokens.push(match[3]);
            }
        }

        return tokens;
    }

    /**
     * Clear all messages
     */
    clearMessages() {
        this._messages = [];
        this._invalidAddressCards = [];
        try {
            sessionStorage.removeItem('invalidAddressCards');
        } catch {
            /* ignore */
        }
        this._persistMessages();
        this._notify();
    }

    replaceMessages(messages) {
        this._messages = Array.isArray(messages) ? messages : [];
        this._persistMessages();
        this._notify();
    }

    updateMessageMetadata(id, updates) {
        let changed = false;
        this._messages = this._messages.map((message) => {
            if (message.id !== id) {
                return message;
            }

            changed = true;
            return {
                ...message,
                ...updates
            };
        });

        if (changed) {
            this._persistMessages();
            this._notify();
        }
    }

    setBusinessRequestContext({
        businessRequestId = this._businessRequestId,
        chatSessionId = this._chatSessionId,
        ownerType = this._ownerType,
        conversationId = this._conversationId,
        resourceApiPlanningApplicationId = this._resourceApiPlanningApplicationId,
        resourceApiAssistConversationId = this._resourceApiAssistConversationId
    }) {
        this._businessRequestId = businessRequestId || null;
        this._chatSessionId = chatSessionId || null;
        this._ownerType = ownerType || null;
        this._resourceApiPlanningApplicationId = resourceApiPlanningApplicationId || null;
        this._resourceApiAssistConversationId = resourceApiAssistConversationId || null;

        if (conversationId) {
            this._conversationId = conversationId;
            this._persistConversationId();
        }

        this._persistBusinessRequestContext();
        this._notify();
    }

    clearBusinessRequestContext() {
        this._businessRequestId = null;
        this._chatSessionId = null;
        this._ownerType = null;
        this._persistBusinessRequestContext();
        this._notify();
    }

    resetConversationContext() {
        this._conversationId = generateUUID();
        this._persistConversationId();
        this.clearBusinessRequestContext();
    }

    hydrateStructuredHistory({ businessRequestId, session, messages }) {
        const chatSessionId = session?.chat_session_id || null;
        const ownerType = session?.owner_type || null;
        const conversationId = session?.conversation_id || this._conversationId;
        // Preserve-on-absence: adopt the server's resourceapi ids only when the
        // history session echoes them; never null an authoritative B0 id because a
        // fetch omitted it (frontend DD §6.3(v) / §7 #5, FE-AC-030).
        const resourceApiPlanningApplicationId =
            session?.resourceapi?.planning_application_id || this._resourceApiPlanningApplicationId;
        const resourceApiAssistConversationId =
            session?.resourceapi?.assist_conversation_id || this._resourceApiAssistConversationId;
        const sortedMessages = [...(messages || [])].sort(compareStructuredHistoryEntries);

        this._messages = sortedMessages.map((message) => {
            const messageType = message.message_type || message.messageType || MESSAGE_TYPES.BOT;
            const isAddressCard = messageType === MESSAGE_TYPES.ADDRESS_CARD;

            return {
                id: message.id || generateUUID(),
                text: message.content || '',
                type: isAddressCard
                    ? MESSAGE_TYPES.ADDRESS_CARD
                    : message.role === 'user'
                      ? MESSAGE_TYPES.USER
                      : MESSAGE_TYPES.BOT,
                sender: isAddressCard ? 'bot' : message.role === 'user' ? 'user' : 'bot',
                timestamp: message.created_at || message.createdAt || new Date().toISOString(),
                persistedMessageId: message.id || null,
                feedback: message.feedback || null,
                businessRequestId: businessRequestId || this._businessRequestId,
                chatSessionId,
                ownerType,
                ...(isAddressCard ? { disabled: true } : {})
            };
        });

        this._conversationId = conversationId;
        this._businessRequestId = businessRequestId || this._businessRequestId;
        this._chatSessionId = chatSessionId;
        this._ownerType = ownerType;
        this._resourceApiPlanningApplicationId = resourceApiPlanningApplicationId;
        this._resourceApiAssistConversationId = resourceApiAssistConversationId;

        this._persistConversationId();
        this._persistBusinessRequestContext();
        this._persistMessages();
        this._notify();
    }

    applyPersistedMessages({
        userMessageId,
        assistantMessageId,
        followUpMessageId = null,
        persistedMessages,
        businessRequestId = this._businessRequestId,
        chatSessionId = this._chatSessionId,
        ownerType = this._ownerType
    }) {
        if (!persistedMessages) {
            return;
        }

        const userPersistedId = persistedMessages.user?.id || null;
        const assistantPersistedId = persistedMessages.assistant?.id || null;
        const assistantFeedback = persistedMessages.assistant?.feedback || null;
        const followUpPersistedId = persistedMessages.followUp?.id || null;

        this._messages = this._messages.map((message) => {
            if (message.id === userMessageId) {
                return {
                    ...message,
                    persistedMessageId: userPersistedId || message.persistedMessageId || null,
                    businessRequestId,
                    chatSessionId,
                    ownerType
                };
            }

            if (message.id === assistantMessageId) {
                return {
                    ...message,
                    persistedMessageId: assistantPersistedId || message.persistedMessageId || null,
                    feedback: assistantFeedback || message.feedback || null,
                    businessRequestId,
                    chatSessionId,
                    ownerType
                };
            }

            if (followUpMessageId && message.id === followUpMessageId) {
                return {
                    ...message,
                    persistedMessageId: followUpPersistedId || message.persistedMessageId || null,
                    businessRequestId,
                    chatSessionId,
                    ownerType
                };
            }

            return message;
        });

        this._persistMessages();
        this._notify();
    }

    markMessagesMigrated({
        messageIds = [],
        persistedMessageIdMap = {},
        businessRequestId = this._businessRequestId,
        chatSessionId = this._chatSessionId,
        ownerType = this._ownerType
    }) {
        if (!Array.isArray(messageIds) || messageIds.length === 0) {
            return;
        }

        const ids = new Set(messageIds);
        let changed = false;

        this._messages = this._messages.map((message) => {
            if (!ids.has(message.id)) {
                return message;
            }

            changed = true;
            // v4/ResourceAPI: prefer the real persisted message id the migrate endpoint
            // returned (mapped local->resourceapi by submit order). Only when that is absent
            // (legacy/Payload migrate) do we fall back to the local id — which is why
            // per-message ResourceAPI ops (feedback) previously 404'd with the local UUID.
            const migratedPersistedId = persistedMessageIdMap[message.id];
            return {
                ...message,
                persistedMessageId: migratedPersistedId || message.persistedMessageId || message.id,
                businessRequestId,
                chatSessionId,
                ownerType
            };
        });

        if (changed) {
            this._persistMessages();
            this._notify();
        }
    }
    // ==================== Loading State ====================

    /**
     * Set loading state
     * @param {boolean} isLoading - Loading state
     */
    setLoading(isLoading) {
        this._isLoading = isLoading;
        this._notify();
    }

    // ==================== Suggestions Management ====================

    /**
     * Load suggestions from localStorage
     */
    _loadSuggestions() {
        const stored = localStore.getJSON(STORAGE_KEYS.SUGGESTIONS);

        if (this._storageContext) {
            if (
                stored &&
                typeof stored === 'object' &&
                stored.version === CHAT_STORAGE_VERSION &&
                areChatStorageContextsEqual(stored.context, this._storageContext) &&
                Array.isArray(stored.suggestions)
            ) {
                this._suggestions = stored.suggestions;
                return;
            }

            if (stored) {
                localStore.remove(STORAGE_KEYS.SUGGESTIONS);
            }
            this._suggestions = [];
            return;
        }

        if (stored && Array.isArray(stored)) {
            this._suggestions = stored;
        }
    }

    /**
     * Save suggestions to localStorage
     */
    _persistSuggestions() {
        if (this._storageContext) {
            localStore.setJSON(STORAGE_KEYS.SUGGESTIONS, {
                version: CHAT_STORAGE_VERSION,
                context: this._storageContext,
                suggestions: this._suggestions
            });
            return;
        }

        localStore.setJSON(STORAGE_KEYS.SUGGESTIONS, this._suggestions);
    }

    /**
     * Set suggestions
     * @param {Array} suggestions - Array of suggestion strings
     */
    setSuggestions(suggestions) {
        this._suggestions = suggestions || [];
        this._persistSuggestions();
        this._notify();
    }

    /**
     * Clear suggestions
     */
    clearSuggestions() {
        this._suggestions = [];
        this._persistSuggestions();
        this._notify();
    }

    /**
     * Get suggestions array
     * @returns {Array} - Suggestions
     */
    get suggestions() {
        return [...this._suggestions];
    }

    // ==================== Genesys Handover ====================

    /**
     * Activate Genesys handover mode
     */
    activateHandover() {
        this._isHandoverActive = true;
        this._genesysSessionToken = generateUUID();
        sessionStore.set(STORAGE_KEYS.GENESYS_SESSION_TOKEN, this._genesysSessionToken);
        this._notify();
    }

    /**
     * Deactivate Genesys handover mode
     */
    deactivateHandover() {
        this._isHandoverActive = false;
        this._genesysSessionToken = null;
        sessionStore.remove(STORAGE_KEYS.GENESYS_SESSION_TOKEN);
        this._notify();
    }

    /**
     * Get messages for Genesys session sync
     * @returns {Array} - Filtered Genesys messages
     */
    getGenesysMessages() {
        if (!this._genesysSessionToken) return [];

        return this._messages.filter(
            (msg) =>
                msg.genesysSessionId === this._genesysSessionToken &&
                [MESSAGE_TYPES.GENESYS_USER, MESSAGE_TYPES.GENESYS_AGENT, MESSAGE_TYPES.GENESYS_BOT].includes(msg.type)
        );
    }

    // ==================== Session Management ====================

    /**
     * Get conversation ID
     * @returns {string} - Conversation ID
     */
    get conversationId() {
        return this._conversationId;
    }

    /**
     * Get active business request ID
     * @returns {string|null}
     */
    get businessRequestId() {
        return this._businessRequestId;
    }

    /**
     * Get active chat session ID
     * @returns {string|null}
     */
    get chatSessionId() {
        return this._chatSessionId;
    }

    /**
     * Get current chat owner type
     * @returns {string|null}
     */
    get ownerType() {
        return this._ownerType;
    }

    /**
     * Get the server-issued ResourceAPI planning application id (v4).
     * @returns {string|null}
     */
    get resourceApiPlanningApplicationId() {
        return this._resourceApiPlanningApplicationId;
    }

    /**
     * Get the server-issued ResourceAPI assist conversation id (v4).
     * Distinct from conversationId (the client UUID).
     * @returns {string|null}
     */
    get resourceApiAssistConversationId() {
        return this._resourceApiAssistConversationId;
    }

    /**
     * Get messages array
     * @returns {Array} - Messages
     */
    get messages() {
        return [...this._messages];
    }
}

// Export singleton instance
export const chatStore = new ChatStore();
